"use client";

/**
 * Every request the project-channel card makes, and the state each one owns.
 *
 * Four writes with four separate busy flags, because they are four different
 * buttons and a single `isPending` would grey out all of them. The two initial
 * READS live in `./use-channel-reads` (gated together, so the "no channel yet"
 * form cannot flash for a deal that has one — and never shown at all when the
 * read failed); this module is the writes and the composed state.
 *
 * Split from the card so the component is markup and this is the API surface.
 */

import { useCallback, useState } from "react";

import {
  createChannelHeadline,
  membershipMessage,
  readMembershipFailures,
  UNLINK_CONFIRM_TEXT,
} from "./membership-message";
import { readErrorMessage, readJson } from "./read-response";
import { useDealProjectChannelReads } from "./use-channel-reads";

export interface DealProjectChannelState {
  /** Slug field for the create form. */
  slug: string;
  setSlug: (slug: string) => void;

  /** True until BOTH initial reads have settled. */
  initializing: boolean;
  hasChannel: boolean;
  channelUrl: string | null;
  linkUrl: string | null;

  /**
   * The channel-status read FAILED, so `hasChannel` is unknown — not false.
   * The card shows this instead of the create form.
   */
  statusError: string | null;
  /** Same, for the existing-chat-link read. */
  linkError: string | null;
  /** Re-run the failed channel-status read. */
  retryStatus: () => void;
  /** Re-run the failed chat-link read. */
  retryLink: () => void;

  creating: boolean;
  reinviting: boolean;
  unlinking: boolean;
  linkLoading: boolean;
  copied: boolean;

  /** Success/partial-success notice for the channel card. */
  channelMsg: string | null;
  /** Whichever write failed last — one error line above both cards. */
  error: string | null;

  createChannel: () => void;
  reinviteOwners: () => void;
  unlinkChannel: () => void;
  generateLink: (regenerate: boolean) => void;
  copyLink: () => void;
}

export function useDealProjectChannel(dealId: string): DealProjectChannelState {
  const reads = useDealProjectChannelReads(dealId);
  const { linkUrl, setLinkUrl, reloadStatus, clearChannel } = reads;

  const [slug, setSlug] = useState("");
  const [creating, setCreating] = useState(false);
  const [reinviting, setReinviting] = useState(false);
  const [unlinking, setUnlinking] = useState(false);
  const [channelMsg, setChannelMsg] = useState<string | null>(null);
  const [linkLoading, setLinkLoading] = useState(false);
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const unlinkChannel = useCallback(() => {
    if (!window.confirm(UNLINK_CONFIRM_TEXT)) return;
    setUnlinking(true);
    setError(null);
    setChannelMsg(null);
    void (async () => {
      try {
        const res = await fetch(`/api/projects/${dealId}/channel`, {
          method: "DELETE",
          credentials: "same-origin",
        });
        const body = await readJson(res);
        if (!res.ok) {
          setError(readErrorMessage(body, "Failed to unlink the project channel."));
          return;
        }
        // Reset local state so the "create channel" form returns.
        clearChannel();
        setSlug("");
        setChannelMsg("Project channel unlinked. You can create a fresh one below.");
      } catch {
        setError("Failed to unlink the project channel.");
      } finally {
        setUnlinking(false);
      }
    })();
  }, [dealId, clearChannel]);

  const createChannel = useCallback(() => {
    setCreating(true);
    setError(null);
    setChannelMsg(null);
    void (async () => {
      try {
        const res = await fetch(`/api/projects/${dealId}/channel`, {
          method: "POST",
          headers: { "content-type": "application/json" },
          credentials: "same-origin",
          body: JSON.stringify({ slug }),
        });
        const body = await readJson(res);
        if (!res.ok) {
          setError(readErrorMessage(body, "Failed to create the project channel."));
          return;
        }
        const alreadyExisted = Boolean((body as { alreadyExisted?: boolean } | null)?.alreadyExisted);
        setChannelMsg(
          membershipMessage(createChannelHeadline(alreadyExisted), readMembershipFailures(body))
        );
        await reloadStatus();
      } catch {
        setError("Failed to create the project channel.");
      } finally {
        setCreating(false);
      }
    })();
  }, [dealId, reloadStatus, slug]);

  const reinviteOwners = useCallback(() => {
    setReinviting(true);
    setError(null);
    setChannelMsg(null);
    void (async () => {
      try {
        const res = await fetch(`/api/projects/${dealId}/channel/reinvite-owners`, {
          method: "POST",
          credentials: "same-origin",
        });
        const body = await readJson(res);
        if (!res.ok) {
          setError(readErrorMessage(body, "Failed to re-add owners to the channel."));
          return;
        }
        setChannelMsg(
          membershipMessage("Owner membership re-checked.", readMembershipFailures(body))
        );
      } catch {
        setError("Failed to re-add owners to the channel.");
      } finally {
        setReinviting(false);
      }
    })();
  }, [dealId]);

  const generateLink = useCallback(
    (regenerate: boolean) => {
      setLinkLoading(true);
      setError(null);
      void (async () => {
        try {
          const res = await fetch(`/api/projects/${dealId}/chat-link`, {
            method: "POST",
            headers: { "content-type": "application/json" },
            credentials: "same-origin",
            body: JSON.stringify(regenerate ? { action: "regenerate" } : {}),
          });
          const body = await readJson(res);
          if (!res.ok) {
            setError(readErrorMessage(body, "Failed to create the chat link."));
            return;
          }
          setLinkUrl((body as { url?: string | null } | null)?.url ?? null);
        } catch {
          setError("Failed to create the chat link.");
        } finally {
          setLinkLoading(false);
        }
      })();
    },
    [dealId, setLinkUrl]
  );

  const copyLink = useCallback(() => {
    if (!linkUrl) return;
    void (async () => {
      try {
        await navigator.clipboard.writeText(linkUrl);
        setCopied(true);
        setTimeout(() => setCopied(false), 1500);
      } catch {
        /* clipboard blocked — user can select manually */
      }
    })();
  }, [linkUrl]);

  return {
    slug,
    setSlug,
    initializing: reads.initializing,
    hasChannel: reads.hasChannel,
    channelUrl: reads.channelUrl,
    linkUrl,
    statusError: reads.statusError,
    linkError: reads.linkError,
    retryStatus: reads.retryStatus,
    retryLink: reads.retryLink,
    creating,
    reinviting,
    unlinking,
    linkLoading,
    copied,
    channelMsg,
    error,
    createChannel,
    reinviteOwners,
    unlinkChannel,
    generateLink,
    copyLink,
  };
}
